import React from 'react';
import { ClipboardCheck, Clock, Award, ChevronRight, FileText, BookOpen, Target } from 'lucide-react';

export default function LearnerAssessments() {
  const upcomingAssessments = [
    {
      id: 1,
      title: 'React Fundamentals Assessment',
      course: 'React Development Masterclass',
      dueDate: '2024-02-15',
      duration: '45 minutes',
      questions: 25,
      status: 'upcoming'
    },
    {
      id: 2,
      title: 'JavaScript Advanced Concepts',
      course: 'Modern JavaScript ES6+',
      dueDate: '2024-02-18',
      duration: '60 minutes',
      questions: 30,
      status: 'upcoming'
    }
  ];

  const completedAssessments = [
    {
      id: 3,
      title: 'HTML & CSS Basics',
      course: 'Web Development Foundations',
      completedDate: '2024-02-10',
      score: 92,
      grade: 'A',
      status: 'completed'
    },
    {
      id: 4,
      title: 'Git Version Control',
      course: 'Developer Tools & Workflows',
      completedDate: '2024-02-08',
      score: 88,
      grade: 'B+',
      status: 'completed'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
        <p className="text-gray-500 mt-1">Track and complete your course assessments</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">{upcomingAssessments.length}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedAssessments.length}</p>
            </div>
            <ClipboardCheck className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Average Score</p>
              <p className="text-2xl font-bold text-gray-900">90%</p>
            </div>
            <Award className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Upcoming Assessments */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Assessments</h2>
        <div className="space-y-3">
          {upcomingAssessments.map((assessment) => (
            <div key={assessment.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{assessment.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{assessment.course}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Due {assessment.dueDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      {assessment.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {assessment.questions} questions
                    </span>
                  </div>
                </div>
                <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  Start Assessment
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Completed Assessments */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Completed Assessments</h2>
        <div className="space-y-3">
          {completedAssessments.map((assessment) => (
            <div key={assessment.id} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{assessment.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{assessment.course}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                    <span>Completed {assessment.completedDate}</span>
                    <span className="font-medium text-green-600">Score: {assessment.score}%</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      Grade: {assessment.grade}
                    </span>
                  </div>
                </div>
                <button className="text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  View Results
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}